import React, { useEffect, useState } from 'react';
import { KeyRound } from 'lucide-react';
import { definirNouveauMotDePasse } from '../context/AuthContext';
import '../components/LoginPage.css';
import './ReinitialisationMotDePasse.css';

export const LONGUEUR_MINIMALE = 8;

/**
 * Lit le fragment que GoTrue ajoute au lien de réinitialisation :
 * `#access_token=…&type=recovery&…`, ou `#error=…&error_code=otp_expired&…`
 * quand le lien est périmé.
 */
export function lireFragment(hash) {
  const params = new URLSearchParams((hash || '').replace(/^#/, ''));
  if (params.get('error') || params.get('error_code')) {
    return { jeton: null, erreur: 'expire' };
  }
  const jeton = params.get('access_token');
  if (!jeton || params.get('type') !== 'recovery') {
    return { jeton: null, erreur: 'absent' };
  }
  return { jeton, erreur: null };
}

function retourConnexion() {
  window.location.assign('/');
}

/**
 * Page publique `/reinitialisation`, cible du lien envoyé par Supabase.
 *
 * Rendue hors de l'application authentifiée : par `LoginPage` quand personne
 * n'est connecté (cas normal), et par la table des routes sinon.
 */
export default function ReinitialisationMotDePasse() {
  // Lecture dans l'initialiseur, pas dans un effet : en StrictMode les effets
  // sont rejoués, et le second passage trouverait le fragment déjà effacé.
  const [{ jeton, erreur: erreurLien }] = useState(() =>
    lireFragment(typeof window !== 'undefined' ? window.location.hash : '')
  );
  const [motDePasse, setMotDePasse] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [erreur, setErreur] = useState(null);
  const [envoi, setEnvoi] = useState(false);
  const [termine, setTermine] = useState(false);

  // Le jeton ne doit rester ni dans la barre d'adresse, ni dans l'historique,
  // ni dans un éventuel en-tête Referer.
  useEffect(() => {
    if (window.location.hash && window.history && window.history.replaceState) {
      window.history.replaceState(
        window.history.state,
        '',
        `${window.location.pathname}${window.location.search}`
      );
    }
  }, []);

  const soumettre = async (e) => {
    e.preventDefault();
    setErreur(null);
    if (motDePasse.length < LONGUEUR_MINIMALE) {
      setErreur(`Le mot de passe doit contenir au moins ${LONGUEUR_MINIMALE} caractères.`);
      return;
    }
    if (motDePasse !== confirmation) {
      setErreur('Les deux mots de passe ne correspondent pas.');
      return;
    }
    setEnvoi(true);
    try {
      await definirNouveauMotDePasse(jeton, motDePasse);
      setTermine(true);
    } catch (err) {
      setErreur(err.message || 'Échec de la réinitialisation');
    } finally {
      setEnvoi(false);
    }
  };

  let contenu;
  if (termine) {
    contenu = (
      <>
        <p className="login-info" role="status">
          Votre mot de passe a été mis à jour. Vous pouvez maintenant vous connecter.
        </p>
        <button type="button" className="login-submit" onClick={retourConnexion}>
          <span>Retour à la connexion</span>
        </button>
      </>
    );
  } else if (!jeton) {
    contenu = (
      <>
        <p className="login-error" role="alert">
          {erreurLien === 'expire'
            ? 'Ce lien a expiré ou a déjà été utilisé. Demandez-en un nouveau depuis l’écran de connexion.'
            : 'Lien de réinitialisation invalide. Utilisez le lien reçu par e-mail.'}
        </p>
        <button type="button" className="login-submit" onClick={retourConnexion}>
          <span>Retour à la connexion</span>
        </button>
      </>
    );
  } else {
    contenu = (
      <form className="login-form" onSubmit={soumettre} noValidate>
        <div className="login-field">
          <label htmlFor="reinit-mot-de-passe">Nouveau mot de passe</label>
          <input
            id="reinit-mot-de-passe"
            type="password"
            autoComplete="new-password"
            value={motDePasse}
            onChange={(e) => setMotDePasse(e.target.value)}
            required
            minLength={LONGUEUR_MINIMALE}
            aria-describedby="reinit-consigne"
          />
        </div>
        <div className="login-field">
          <label htmlFor="reinit-confirmation">Confirmer le mot de passe</label>
          <input
            id="reinit-confirmation"
            type="password"
            autoComplete="new-password"
            value={confirmation}
            onChange={(e) => setConfirmation(e.target.value)}
            required
            minLength={LONGUEUR_MINIMALE}
          />
        </div>
        <p id="reinit-consigne" className="login-consigne">
          Au moins {LONGUEUR_MINIMALE} caractères.
        </p>

        {erreur && (
          <p className="login-error" role="alert">
            {erreur}
          </p>
        )}

        <button type="submit" className="login-submit" disabled={envoi}>
          <KeyRound size={20} aria-hidden />
          <span>{envoi ? 'Enregistrement…' : 'Enregistrer le mot de passe'}</span>
        </button>
      </form>
    );
  }

  return (
    <div className="login-page">
      <main className="login-card" role="main">
        <div className="login-brand">
          <div className="login-brand-icon" aria-hidden>
            <KeyRound size={24} />
          </div>
          <div>
            <h1 className="login-title">Biblio-Tech RAG</h1>
            <p className="login-subtitle">Réinitialisation du mot de passe</p>
          </div>
        </div>
        {contenu}
      </main>
    </div>
  );
}
